import { supabase } from '../config/supabase';

/**
 * Make sure a signed-in user has a row in user_profiles, carrying over their
 * old Quick Join identity if one exists for the same address.
 *
 * Historically this app had two parallel identity stores: `user_profiles` for
 * password accounts and `email_users` for unverified "Quick Join" users. Moving
 * to passwordless collapses those into one. Votes and starred movies already
 * key on the email address, so they follow automatically - the only things that
 * need carrying across are the display name and avatar.
 *
 * Runs on every sign-in and is idempotent: if a profile already exists it does
 * nothing. Never throws - a failure here must not block signing in.
 *
 * @param {object} user a Supabase auth user
 * @returns {Promise<{created: boolean, migrated: boolean, error: Error|null}>}
 */
export const ensureUserProfile = async (user) => {
  const result = { created: false, migrated: false, error: null };

  if (!user?.id || !user?.email) return result;
  const email = user.email.toLowerCase();

  try {
    const { data: existing, error: lookupError } = await supabase
      .from('user_profiles')
      .select('user_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (lookupError) throw lookupError;
    if (existing) return result; // already migrated / already had an account

    // Look for a Quick Join profile at the same address.
    const { data: legacy } = await supabase
      .from('email_users')
      .select('full_name, avatar_url')
      .eq('email', email)
      .maybeSingle();

    // Precedence: the name they chose in this app beats the one the OAuth
    // provider supplies, which in turn beats nothing.
    const meta = user.user_metadata || {};
    const fullName = legacy?.full_name || meta.full_name || meta.name || null;
    const avatarUrl = legacy?.avatar_url || meta.avatar_url || meta.picture || null;

    const now = new Date().toISOString();
    const { error: insertError } = await supabase
      .from('user_profiles')
      .insert([{
        user_id: user.id,
        email: user.email,
        full_name: fullName,
        avatar_url: avatarUrl,
        is_admin: false,
        created_at: now,
        updated_at: now
      }]);

    if (insertError) throw insertError;

    result.created = true;
    result.migrated = !!legacy;

    if (result.migrated) {
      console.log('✅ Carried over Quick Join profile for', email);
    } else {
      console.log('✅ Created user profile for', email);
    }
  } catch (err) {
    // Signing in must still succeed even if the profile write fails.
    console.warn('⚠️ ensureUserProfile failed (sign-in unaffected):', err.message);
    result.error = err;
  }

  return result;
};

export default ensureUserProfile;
